import React, { useEffect } from 'react';
import {
  Drawer, Box, Typography, IconButton, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Divider, Alert, Tooltip,
} from '@mui/material';
import { Close as CloseIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useFetchDrillDownMutation } from '../../../redux/wallboardApi';
import { useDashboardContext } from '../pages/DashboardViewPage';

export interface DrillDownTarget {
  dashboardId: string;
  widgetId: string;
  widgetName: string;
  drillDownValue?: string;      // e.g. the supplier name clicked in a bar chart
  timeRange?: { start: string; end: string };
}

interface DrillDownDrawerProps {
  target: DrillDownTarget | null;
  onClose: () => void;
}

const CURRENCY_COLS = new Set(['value', 'balance', 'fulfilledValue', 'backOrderValue']);
const DATE_COLS     = new Set(['date']);

function formatCell(col: { id: string; label: string }, val: any): React.ReactNode {
  if (val === null || val === undefined) return <span style={{ color: '#aaa' }}>—</span>;

  if (CURRENCY_COLS.has(col.id)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(val);
  }

  if (DATE_COLS.has(col.id)) {
    const d = new Date(val);
    return isNaN(d.getTime()) ? val : d.toLocaleDateString('en-ZA', { dateStyle: 'medium' });
  }

  // Status chips
  if (col.id === 'status' || col.id === 'paymentStatus') {
    const color: Record<string, 'error' | 'warning' | 'success' | 'info' | 'default'> = {
      Rejected: 'error',
      Cancelled: 'error',
      'Not Yet Paid': 'warning',
      'Partially Paid': 'warning',
      'Fully Paid': 'success',
      Approved: 'success',
      Completed: 'success',
    };
    return (
      <Chip
        label={val}
        size="small"
        color={color[val] ?? 'default'}
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
    );
  }

  if (col.id === 'priority') {
    const color: Record<string, 'error' | 'warning' | 'default'> = {
      Critical: 'error', Urgent: 'warning', Standard: 'default',
    };
    return (
      <Chip
        label={val}
        size="small"
        color={color[val] ?? 'default'}
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
    );
  }

  return String(val);
}

export const DrillDownDrawer: React.FC<DrillDownDrawerProps> = ({ target, onClose }) => {
  const { fontSize } = useDashboardContext();
  const [fetchDrillDown, { data, isLoading, error, reset }] = useFetchDrillDownMutation();

  useEffect(() => {
    if (!target) { reset(); return; }
    fetchDrillDown({
      dashboardId: target.dashboardId,
      widgetId: target.widgetId,
      drillDownValue: target.drillDownValue,
      timeRange: target.timeRange,
      limit: 50,
    });
  }, [target]);

  const title = target
    ? `${target.widgetName}${target.drillDownValue ? ` — ${target.drillDownValue}` : ''}`
    : '';

  return (
    <Drawer
      anchor="right"
      open={!!target}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 680, md: 780 },
          bgcolor: '#f8fafc',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3, py: 2,
          background: 'linear-gradient(135deg, #0C1F3F 0%, #1a365d 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 64,
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="white"
            sx={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: `${0.8 * fontSize}rem` }}
          >
            Drill-Down
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.75)" sx={{ fontSize: `${0.75 * fontSize}rem` }} noWrap>
            {title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Body */}
      <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load records. Please try again.
          </Alert>
        )}

        {data && !isLoading && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {data.records.length} of {data.total.toLocaleString()} records
              </Typography>
              {data.total > 50 && (
                <Typography variant="caption" color="text.secondary">
                  (Top 50 shown — open full list for more)
                </Typography>
              )}
            </Box>

            {data.records.length === 0 ? (
              <Alert severity="info">No records found for this selection.</Alert>
            ) : (
              <TableContainer
                sx={{
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  bgcolor: 'white',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {data.columns.map((col) => (
                        <TableCell
                          key={col.id}
                          sx={{
                            bgcolor: '#1a365d',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: `${0.75 * fontSize}rem`,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                            py: 1,
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.records.map((row, i) => (
                      <TableRow
                        key={row.id ?? i}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: '#f8fafc' },
                          '&:hover': { bgcolor: '#e8f0fe' },
                          transition: 'background 0.15s',
                        }}
                      >
                        {data.columns.map((col) => (
                          <TableCell
                            key={col.id}
                            sx={{
                              fontSize: `${0.78 * fontSize}rem`,
                              py: 0.75,
                              color: '#2d3748',
                              whiteSpace: col.id === 'title' ? 'normal' : 'nowrap',
                              maxWidth: col.id === 'title' ? 200 : undefined,
                            }}
                          >
                            {formatCell(col, row[col.id])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};
